/**
 * The one set of words the library uses for where a file stands, in the
 * add-in and the web app alike (asthra-v4-web/src/libraries/status-words.ts
 * says the same). A writer only needs to know whether a draft can use the
 * file yet and, if not, what to do.
 */
import type { DocumentRow, ProcessingStatus } from "./types";

export const STATUS_WORD = {
  waitingToUpload: "Waiting to upload",
  uploading: "Uploading",
  notConfirmed: "Not confirmed",
  notAccepted: "Not accepted",
  alreadyInLibrary: "Already in library",
  didNotUpload: "Didn't upload",
  waitingToRead: "Waiting to read",
  reading: "Reading",
  ready: "Ready",
  couldNotRead: "Couldn't read",
  uploadStopped: "Upload stopped",
} as const;

/** The word for a library row's ``processing_status``. */
export function rowStatusWord(status: ProcessingStatus, upstream?: string | null): string {
  switch (status) {
    case "uploading":
      return STATUS_WORD.uploading;
    case "indexing":
    case "classifying":
      return (upstream ?? "").trim().toLowerCase() === "pending" ? STATUS_WORD.waitingToRead : STATUS_WORD.reading;
    case "ready":
    case "limited":
      return STATUS_WORD.ready;
    case "failed_upload":
      return STATUS_WORD.didNotUpload;
    case "failed_processing":
      return STATUS_WORD.couldNotRead;
  }
}

/** Known reading steps, said plainly. The raw string stays in the CSV. */
const READ_CAUSE: readonly (readonly [RegExp, string])[] = [
  [/no text layer|ocr/i, "No text layer (scanned image)"],
  [/timed? ?out|timeout/i, "Reading took too long"],
  [/understanding document|parse/i, "The contents of this file couldn't be understood"],
];

/** Why a file couldn't be read, in plain words; never the raw backend string. */
export function couldNotReadCause(upstream: string | null | undefined): string {
  const step = (upstream ?? "").replace(/^Failed:\s*/i, "");
  for (const [pattern, cause] of READ_CAUSE) if (pattern.test(step)) return cause;
  return "Something went wrong while reading this file";
}

export type LibraryFilter = "all" | "ready" | "reading" | "problems";

export const LIBRARY_FILTER_LABEL: Record<LibraryFilter, string> = {
  all: "All",
  ready: "Ready",
  reading: "Reading",
  problems: "Problems",
};

export function rowFilter(row: Pick<DocumentRow, "processing_status">): Exclude<LibraryFilter, "all"> {
  switch (row.processing_status) {
    case "ready":
    case "limited":
      return "ready";
    case "failed_upload":
    case "failed_processing":
      return "problems";
    default:
      return "reading";
  }
}
